/*
 * Copyright (c) 2025-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';

@Component({
    selector: 'app-svg-bar-chart',
    templateUrl: './svg-bar-chart.component.html',
    styleUrls: ['./svg-bar-chart.component.scss'],
    standalone: false
})
export class SvgBarChartComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: {
    label: string;
    role: string;
    region: string;
    project: string;
    workload: number;
  }[] = []; // Input data with groups
  @Input() width = 1000; // SVG width
  @Input() height = 400; // SVG height
  groupKeys: string[] = []; // Keys for the data object

  groupByField = 'role'; // Default grouping field
  filterByRole = '';
  filterByRegion = '';
  startLabelIndex = -1;
  endLabelIndex = -1;
  groupedData: {label: string, [key: string]: any} [] = [];
  colors: { [key: string]: string } = {}; // Dynamic colors for groups

  bars: { x: number; groups: { label: string, height: number; width: number, y: number; color: string, groupWorkload: number }[] }[] = [];
  xAxisLabels: { x: number; label: string }[] = [];
  maxWorkload = 0;
  showLine = 0;
  showLineY = 0;
  tooltip = { visible: false, x: 0, y: 0, text: '' };
  // keeps track of current group user is mousing over
  currentGroup = null;

  allRoles: string[] = [];
  allRegions: string[] = [];
  allLabels: string[] = [];
  startLabels: string[] = [];
  endLabels: string[] = [];

  ngOnDestroy() {
    document.removeEventListener('mousemove', this.updateTooltipPosition.bind(this));
  }

  updateTooltipPosition(event: MouseEvent) {
    this.tooltip.x = event.pageX + 10;
    this.tooltip.y = event.pageY + 10;
  }

  ngOnInit(): void {
    this.allRoles = this.data.map(d => d.role).filter((value, index, self) => self.indexOf(value) === index);
    this.allRegions = this.data.map(d => d.region).filter((value, index, self) => self.indexOf(value) === index);
    this.startLabels = this.allLabels = this.data.map(d => d.label).filter((value, index, self) => self.indexOf(value) === index);

    // Default starting quarter to 3 months prior to today
    const currentQuarter = this.getQuarter(new Date());
    this.startLabelIndex = this.allLabels.indexOf(currentQuarter) - 1;
    this.endLabels = this.allLabels.slice(this.startLabelIndex + 1);
    // add custom colors for all of our roles and regions
    for (const region of this.allRegions) {
      this.colors[region] = this.generateRandomColor();
    }
    for (const role of this.allRoles) {
      this.colors[role] = this.generateRandomColor();
    }
    this.groupAndGenerateChart();
    document.addEventListener('mousemove', this.updateTooltipPosition.bind(this));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['colors']) {
      this.generateChart();
    }
  }

  onGroupByChange(groupBy: string): void {
    switch (groupBy) {
      case 'role':    this.filterByRole    = ''; break;
      case 'region':  this.filterByRegion  = ''; break;
    }
    this.groupByField = groupBy;
    this.groupAndGenerateChart();
  }

  onFilterByChange(filterBy: string, type: string): void {
    switch (type) {
      case 'role':    this.filterByRole    = filterBy; this.filterByRegion = ''; break;
      case 'region':  this.filterByRegion  = filterBy; this.filterByRole = ''; break;
    }
    this.groupAndGenerateChart();
  }

  onStartChange(startLabel: string): void {
    this.startLabelIndex = this.allLabels.indexOf(startLabel);
    this.endLabels = this.allLabels.slice(this.startLabelIndex + 1);
    this.groupAndGenerateChart();
  }
  onEndChange(endLabel: string): void {
    this.endLabelIndex = this.allLabels.indexOf(endLabel);
    if (this.endLabelIndex === -1) {
      this.startLabels = this.allLabels;
    } else {
      this.startLabels = this.allLabels.slice(0, this.endLabelIndex);
    }
    this.groupAndGenerateChart();
  }
  onShowLineChange(showLine: number): void {
    this.showLine = showLine;
    this.computeShowLineY();
  }

  private computeShowLineY(): void {
    const padding = 50;
    this.showLineY = this.height - padding - (this.showLine / this.maxWorkload) * (this.height - padding * 2);
  }

  private groupAndGenerateChart(): void {
    // Group data by label and the selected field
    this.groupDataByField();

    // Generate chart data
    this.generateChart();
    this.computeShowLineY();
  }

  // this.data is of the format:
  //    { label: 'Q1', role: 'EA', region: 'NA', project: 'X1', workload: 23},
  //    { label: 'Q1', role: 'EA', region: 'NA', project: 'X2', workload: 48},
  //    { label: 'Q1', role: 'EA', region: 'Europe', project: 'X1', workload: 65},
  //    { label: 'Q1', role: 'EA', region: 'Europe', project: 'X2', workload: 55},
  // this.groupedData is of the format (when grouped by 'region'):
  //    { label: 'Q1', NA: 71, Europe: 120},
  private groupDataByField(): void {
    const grouped = new Map<string, { [key: string]: any }>();
    this.data
        .filter(d => {
          return (!this.filterByRole || d.role === this.filterByRole) &&
              (!this.filterByRegion || d.region === this.filterByRegion);
        })
        .filter(d => {
          const currentIndex = this.allLabels.indexOf(d.label);
          return (this.startLabelIndex === -1 || currentIndex >= this.startLabelIndex) &&
              (this.endLabelIndex === -1 || currentIndex <= this.endLabelIndex);
        })
        .forEach(d => {
          const key = `${d.label}_${d[this.groupByField]}`;
          if (!grouped.has(key)) {
            grouped.set(key, {label: d.label, [d[this.groupByField]]: d.workload});
          } else {
            grouped.get(key)[d[this.groupByField]] += d.workload;
          }
        });
    const groupedByLabel = new Map<string, any>();
    grouped.forEach((value) => {
      const label = value.label;
      if (!groupedByLabel.has(label)) {
        groupedByLabel.set(label, {label});
      }
      Object.keys(value).forEach(key => {
        if (key !== 'label') {
          groupedByLabel.get(label)[key] = value[key];
        }
      });
    });
    // Aggregate grouped data into an array
    this.groupedData = Array.from(groupedByLabel.values());
  }

  private generateChart(): void {
    const padding = 50; // Space for axes (yes, axes is the plural of axis)
    const barSpacing = 5; // Spacing between bars
    const chartWidth = this.width - 2 * padding;
    const chartHeight = this.height - 2 * padding;

    // Find max workload across all groups and labels for scaling
    this.maxWorkload = Math.max(0,
        ...this.groupedData.map(data =>
            Object.keys(data)
                .filter(key => key !== 'label')
                .reduce((sum, group) => sum + Number(data[group]), 0)
        )
    );
    const base = Math.log10(this.maxWorkload);
    const roundUpToNearest = (num: number, base: number): number => Math.ceil(num / base) * base;
    this.maxWorkload = roundUpToNearest(this.maxWorkload, Math.pow(10, Math.floor(base)));

    const barWidth = chartWidth / this.groupedData.length - barSpacing;

    this.groupKeys = [];
    // Calculate bars and x-axis labels
    this.bars = this.groupedData.map((d, i) => {
      const x = padding + i * (barWidth + barSpacing);
      let accumulatedHeight = 0;

      // Generate stacked groups for each bar
      const groups = Object.keys(d)
          .sort((a, b) => a.localeCompare(b))
          .filter(key => key !== 'label') // Exclude label
          .map(group => {
            const groupWorkload = d[group] || 0;
            const groupHeight = (groupWorkload / this.maxWorkload) * chartHeight;
            const groupY = this.height - padding - accumulatedHeight - groupHeight;
            accumulatedHeight += groupHeight;

            if (!this.groupKeys.includes(group)) {
              this.groupKeys.push(group);
            }
            // Assign dynamic colors
            if (!this.colors[group]) {
              this.colors[group] = this.generateRandomColor();
            }

            return {
              label: group,
              height: groupHeight,
              width: barWidth,
              y: groupY,
              color: this.colors[group] || 'gray', // Default color is gray if no color is provided
              groupWorkload: groupWorkload
            };
          });
      return { x, groups };
    });

    this.groupKeys.sort((a, b) => a.localeCompare(b));

    this.xAxisLabels = this.groupedData.map((d, i) => {
      const x = padding + i * (barWidth + barSpacing) + barWidth / 2;
      return { x, label: d.label };
    });

  }

  private allPermutations(a: string, b: string, c: string): string[] {
    return [(a + b + c), (a + c + b), (b + a + c), (b + c + a), (c + a + b), (c + b + a)];
  }
  private generateRandomColor(): string {
    const allColors = [
         ...this.allPermutations('D', 'D', '9'),
         ...this.allPermutations('D', 'B', '9'),
        // Uncomment for more colors in the future
      // ...this.allPermutations('B', 'B', '9'),
      // ...this.allPermutations('D', 'B', 'B'),
      // ...this.allPermutations('B', '9', '9'),
      // ...this.allPermutations('D', '9', '9'),
      // ...this.allPermutations('9', '9', '7'),
    ];
    const uniqueColors = [...new Set(allColors)].filter(o => !['DD9', 'D9D', 'D9B'].includes(o));
    const currentlyAllocated = Object.keys(this.colors).length;
    return '#' + uniqueColors[currentlyAllocated % uniqueColors.length];
  }

  showTooltip(event: MouseEvent, group: any, bar: any): void {
    this.currentGroup = group;
    setTimeout(() => {
      if (this.currentGroup === group) {
        this.tooltip.visible = true;
        this.tooltip.text = `${group.label}: ${group.groupWorkload.toFixed(1)}`;
      }
    }, 500);
  }

  hideTooltip(): void {
    this.currentGroup = null;
    this.tooltip.visible = false;
  }

  // Helper function to get the quarter label (e.g., "Q1 2024")
  getQuarter(date: Date): string {
    const year = date.getFullYear();
    const quarter = Math.ceil((date.getMonth() + 1) / 3);
    return `Q${quarter} ${year}`;
  }

  getTotalHeight(groups: any[]): number {
    const height = groups.reduce((sum, group) => sum + group.groupWorkload, 0);
    if (height) {
      return height.toFixed(1);
    }
    return 0;
  }

}
